/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import classnames from 'classnames';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import './PerformanceCalibration.scss';
import listView from '../../assets/images/calibration/listView.png';
import performancePCM from '../../assets/images/calibration/performancePCM.png';
import overallRating from '../../assets/images/calibration/overallRating.png';
import OverallRating from './components/OverallRating/OverallRating';
import PerformanceMatrix from './components/PerformanceMatrix/PerformanceMatrix';
import EmployeeListView from './components/EmployeeListView/EmployeeListView';
import FinalizeConfirmModal from './components/OverallRating/FinalizeConfirmModal';
import {
  calibrationBySessionId,
  calibrationMatrix,
  updateCalibrationSubject,
  updateOverAllRating,
} from '../../store/apis/calibrationService';
import { getAuthUser } from '../../utils/auth.util';
import CalibrationFilterModal from './components/CalibrationFilterModal/CalibrationFilterModal';
import { fetchCalibrationWeightageNDueDate } from '../../store/apis/performanceService';

function Footer({ navigate, toggleFinalizeModal, onSave, sessionStatus }) {
  return (
    <div className="performance-footer">
      <Button
        className="btn custom-light-btn"
        onClick={() => navigate('/hr/calibration')}
      >
        Close
      </Button>
      {!sessionStatus && (
        <>
          <Button
            className="btn custom-light-btn"
            onClick={() => {
              onSave('Save_Draft');
            }}
          >
            Save
          </Button>
          <Button className="btn custom-btn" onClick={toggleFinalizeModal}>
            Finalize
          </Button>
        </>
      )}
    </div>
  );
}

function PerformanceCalibration() {
  const navigate = useNavigate();
  const location = useLocation();
  const activeLocationTab = location?.state?.activeTab
    ? location.state.activeTab?.toString()
    : '1';
  const { id: sessionId } = useParams();
  const user = getAuthUser();
  const [sessionStatus, setSessionStatus] = useState(false);
  const [activeTab, setActiveTab] = useState(activeLocationTab);
  const [openFinalizeModal, setOpenFinalizeModal] = useState(false);
  const [calibrationFilterModal, setCalibrationFilterModal] = useState(false);
  const [filterData, setFilterData] = useState({});

  const toggleCalibrationFilterModal = () => {
    setCalibrationFilterModal(!calibrationFilterModal);
  };

  const {
    mutate: getCalibrationCall,
    data: calibrationData,
    isPending,
  } = useMutation({
    mutationFn: calibrationBySessionId,
    onSuccess: data => {
      const response = data?.result;
      setSessionStatus(
        response?.Session_Status === 'Completed' ||
          response?.Session_Status === 'Cancelled' ||
          response?.Session_Status === 'Released',
      );
    },
    onError: error => {
      toast.error(error?.message || 'Failed to fetch calibration data.', {
        className: 'toast-error',
      });
    },
  });

  const {
    mutate: getCalibrationMatrixEmp,
    data: calibrationMatrixEmp,
    isPending: calibrationMatrixEmpIsPending,
  } = useMutation({
    mutationFn: calibrationMatrix,
    onError: error => {
      toast.error(
        error?.message || 'Failed to fetch calibration matrix employees.',
        {
          className: 'toast-error',
        },
      );
    },
  });

  const {
    mutate: getCalibrationMatrix,
    data: calibrationMatrixData,
    isPending: calibrationMatrixIsPending,
  } = useMutation({
    mutationFn: calibrationMatrix,
    onError: error => {
      toast.error(error?.message || 'Failed to fetch calibration matrix.', {
        className: 'toast-error',
      });
    },
  });

  const {
    mutate: getCalibrationPercentage,
    data: calibrationPercentageData,
    isPending: calibrationPercentageIsPending,
  } = useMutation({
    mutationFn: calibrationMatrix,
    onError: error => {
      toast.error(error?.message || 'Failed to fetch calibration percentage.', {
        className: 'toast-error',
      });
    },
  });

  const { data: weightagePercentage, refetch: refetchWeightage } = useQuery({
    queryKey: ['weightagePercentage', calibrationData],
    queryFn: () =>
      fetchCalibrationWeightageNDueDate({
        type: 'weightage',
        fiscalYear: calibrationData?.result?.ReviewPrd?.trim(),
      }),
    enabled: false, // Disable automatic execution
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Add useEffect to trigger fetch when calibrationData changes
  useEffect(() => {
    if (calibrationData?.result?.ReviewPrd) {
      refetchWeightage();
    }
  }, [calibrationData?.result?.ReviewPrd, refetchWeightage]);

  const callAPIs = useCallback(() => {
    getCalibrationMatrix({ sessionId, status: 'Matrix' });

    getCalibrationMatrixEmp({ sessionId, status: 'Matrix_Employee' });

    getCalibrationPercentage({ sessionId, status: 'Percentage' });
  }, [
    sessionId,
    getCalibrationMatrix,
    getCalibrationMatrixEmp,
    getCalibrationPercentage,
  ]);

  useEffect(() => {
    if (sessionId) {
      getCalibrationCall({ sessionId });
      getCalibrationMatrix({ sessionId, status: 'Matrix' });
      getCalibrationMatrixEmp({ sessionId, status: 'Matrix_Employee' });
      getCalibrationPercentage({ sessionId, status: 'Percentage' });
    }
  }, [sessionId]);

  const { mutate: updateRating } = useMutation({
    mutationFn: updateOverAllRating,
    onSuccess: () => {
      callAPIs();
      toast.success('Calibration updated successfully!', {
        className: 'toast-success',
      });
      navigate('/hr/calibration');
    },
  });

  const toggleFinalizeModal = () => {
    setOpenFinalizeModal(!openFinalizeModal);
  };

  const onSave = async type => {
    updateRating({
      sessionId,
      userId: user?.id,
      status: type,
    });
  };

  const handleFinalize = () => {
    onSave('Finalise');
    toggleFinalizeModal();
  };

  const { mutate: updateSubject, isPending: updateSubjectIsPending } =
    useMutation({
      mutationFn: updateCalibrationSubject,
      onSuccess: () => {
        const payload = {
          sessionId,
          companyCode: filterData?.companyCode?.Code?.trim(),
          participantId: filterData?.manager?.code?.trim(),
          subjectId: filterData?.employee?.code?.trim(),
          departmentId: filterData?.department?.Code?.trim(),
          classificationId: filterData?.classification?.value?.trim(),
        };
        getCalibrationCall(payload);
      },
      onError: error => {
        toast.error(error?.message || 'Failed to update calibration subject.', {
          className: 'toast-error',
        });
      },
    });

  const refetchAllData = useCallback(() => {
    if (sessionId) {
      const payload = {
        sessionId,
        companyCode: filterData?.companyCode?.Code?.trim(),
        participantId: filterData?.manager?.code?.trim(),
        subjectId: filterData?.employee?.code?.trim(),
        departmentId: filterData?.department?.Code?.trim(),
        classificationId: filterData?.classification?.value?.trim(),
      };

      getCalibrationCall(payload);
      getCalibrationMatrix({ status: 'Matrix', ...payload });
      getCalibrationMatrixEmp({ status: 'Matrix_Employee', ...payload });
      getCalibrationPercentage({ status: 'Percentage', ...payload });
    }
  }, [
    sessionId,
    filterData,
    getCalibrationCall,
    getCalibrationMatrix,
    getCalibrationMatrixEmp,
    getCalibrationPercentage,
  ]);

  const setFilterDataItem = data => {
    const payload = {
      sessionId,
      companyCode: data?.companyCode?.Code.trim(),
      participantId: data?.manager?.code.trim(),
      subjectId: data?.employee?.code.trim(),
      departmentId: data?.department?.Code.trim(),
      classificationId: data?.classification?.value.trim(),
    };
    getCalibrationCall(payload);
    getCalibrationMatrix({ status: 'Matrix', ...payload });
    getCalibrationMatrixEmp({ status: 'Matrix_Employee', ...payload });
    getCalibrationPercentage({ status: 'Percentage', ...payload });
    setFilterData(data);
  };

  return (
    <div className="page-content pc-content-page px-0">
      <Nav
        tabs
        className="calibration-tabs d-flex flex-row justify-content-start"
      >
        <NavItem>
          <NavLink
            className={classnames({
              active: activeTab === '1',
              'cursor-pointer': true,
            })}
            onClick={() => setActiveTab('1')}
          >
            <div className="tab-title">
              <img src={overallRating} alt="" />
              <span>
                <strong>Overall Rating</strong>
              </span>
            </div>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({
              active: activeTab === '2',
              'cursor-pointer': true,
            })}
            onClick={() => setActiveTab('2')}
          >
            <div className="tab-title">
              <img src={performancePCM} alt="" />
              <span>
                <strong>PCM vs Performance</strong>
              </span>
            </div>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({
              active: activeTab === '3',
              'cursor-pointer': true,
            })}
            onClick={() => setActiveTab('3')}
          >
            <div className="tab-title">
              <img src={listView} alt="" />
              <span>
                <strong>List View</strong>
              </span>
            </div>
          </NavLink>
        </NavItem>
      </Nav>
      <TabContent activeTab={activeTab}>
        <TabPane tabId="1">
          <OverallRating
            refetchAllData={refetchAllData}
            setSessionStatus={setSessionStatus}
            calibrationData={calibrationData}
            isPending={isPending}
            isReadOnly={sessionStatus}
            toggle={toggleCalibrationFilterModal}
            filterData={filterData}
            resetFilterData={setFilterDataItem}
            updateSubject={updateSubject}
            updateSubjectIsPending={updateSubjectIsPending}
          />
        </TabPane>
        <TabPane tabId="2">
          <PerformanceMatrix
            refetchAllData={refetchAllData}
            calibrationMatrixData={calibrationMatrixData}
            calibrationMatrixIsPending={calibrationMatrixIsPending}
            calibrationMatrixEmp={calibrationMatrixEmp}
            calibrationMatrixEmpIsPending={calibrationMatrixEmpIsPending}
            toggleFilter={toggleCalibrationFilterModal}
            filterData={filterData}
            resetFilterData={setFilterDataItem}
            weightagePercentage={weightagePercentage}
            reviewPrd={calibrationData?.result?.ReviewPrd?.trim()}
            isReadOnly={sessionStatus}
          />
        </TabPane>
        <TabPane tabId="3">
          <EmployeeListView
            calibrationPercentageData={calibrationPercentageData}
            calibrationPercentageIsPending={calibrationPercentageIsPending}
            toggle={toggleCalibrationFilterModal}
            filterData={filterData}
            resetFilterData={setFilterDataItem}
          />
        </TabPane>
        <Footer
          navigate={navigate}
          toggleFinalizeModal={toggleFinalizeModal}
          onSave={onSave}
          sessionStatus={sessionStatus}
        />
      </TabContent>
      <FinalizeConfirmModal
        isOpen={openFinalizeModal}
        toggle={toggleFinalizeModal}
        onYes={handleFinalize}
      />
      <CalibrationFilterModal
        isOpen={calibrationFilterModal}
        toggle={toggleCalibrationFilterModal}
        data={filterData}
        filterData={setFilterDataItem}
      />
    </div>
  );
}

export default PerformanceCalibration;
